import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  SpeakingLevel,
  UserFragmentFragment,
  UserListSortDirection,
  UserListSortField,
} from "../gql/graphql.ts";
import { SpeakingLevelWithGroup } from "./SpeakingLevelWithGroup.tsx";
import { BasicTable } from "../UI/Table/BasicTable.tsx";
import {
  ArrowTopRightOnSquareIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  ClipboardIcon,
  EllipsisVerticalIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline/index.js";
import { useCopyToClipboard } from "../UI/Feedback/useCopyToClipboard.ts";
import { PrimaryButton } from "../UI/PrimaryButton.tsx";
import { json2csv } from "json-2-csv";
import { Menu } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import {
  SortDirection,
  SortLabels,
  TableColumnHeader,
} from "../UI/Table/TableColumnHeader.tsx";
import { NumberRangeFilter } from "../UI/Table/Filters/NumberRangeFilter.tsx";
import { CheckboxListFilter } from "../UI/Table/Filters/CheckboxListFilter.tsx";
import { isEmptyRange } from "../UI/Table/numberRange.ts";
import {
  SPEAKING_LEVELS_FROM_HIGHEST,
  UserActivityCounterName,
  UserTableFilters,
  UserTableSort,
} from "./userTableState.ts";
import { ActivePlansOfUser } from "./useUserActivePlans.ts";

type SortAndFilterControls = {
  sort: UserTableSort;
  onSortChange: (sort: UserTableSort) => void;
  filters: UserTableFilters;
  onFiltersChange: (filters: UserTableFilters) => void;
};

type Props = {
  users: UserFragmentFragment[];
  onRowClick: (user: UserFragmentFragment) => void;
  enableCsvExport?: boolean;
  sortAndFilterControls?: SortAndFilterControls;
  activePlansOf?: ActivePlansOfUser;
};

const EMAIL_SORT_LABELS: SortLabels = {
  asc: "Email A → Z",
  desc: "Email Z → A",
};
const NUMERIC_SORT_LABELS: SortLabels = {
  asc: "Lowest first",
  desc: "Highest first",
};

const SPEAKING_LEVEL_OPTIONS = SPEAKING_LEVELS_FROM_HIGHEST.map((level) => ({
  value: level,
  label: level,
}));

const ACTIVITY_COUNTER_COLUMNS: {
  id: string;
  header: string;
  description: string;
  sortField: UserListSortField;
  counterName: UserActivityCounterName;
  countOf: (user: UserFragmentFragment) => number;
}[] = [
  {
    id: "receivedLessonFeedbackCount",
    header: "Lesson feedback",
    description: "Lesson feedbacks received from teachers",
    sortField: UserListSortField.ReceivedLessonFeedbackCount,
    counterName: "receivedLessonFeedbackCount",
    countOf: (user) => user.lessonFeedbacks.length,
  },
  {
    id: "receivedProgressCheckFeedbackCount",
    header: "PC feedback",
    description: "Progress check feedbacks received",
    sortField: UserListSortField.ReceivedProgressCheckFeedbackCount,
    counterName: "receivedProgressCheckFeedbackCount",
    countOf: (user) => user.progressCheckResults.length,
  },
  {
    id: "no-show",
    header: "No show",
    description: "Lessons where the user did not show up",
    sortField: UserListSortField.NoShowCount,
    counterName: "noShowCount",
    countOf: (user) =>
      user.lessonFeedbacks.filter((feedback) => feedback.noShow).length,
  },
  {
    id: "progressCheckCount",
    header: "PC",
    description: "Progress checks attended",
    sortField: UserListSortField.ParticipatedProgressCheckCount,
    counterName: "participatedProgressCheckCount",
    countOf: (user) => user.partecipatedProgressCheckCount,
  },
  {
    id: "speakingClassCount",
    header: "RLC",
    description: "Real life conversations attended",
    sortField: UserListSortField.ParticipatedSpeakingClassCount,
    counterName: "participatedSpeakingClassCount",
    countOf: (user) => user.partecipatedSpeakingClassCount,
  },
];

function toSortDirection(direction: UserListSortDirection): SortDirection {
  return direction === UserListSortDirection.Asc ? "asc" : "desc";
}

function toUserListSortDirection(
  direction: SortDirection,
): UserListSortDirection {
  return direction === "asc"
    ? UserListSortDirection.Asc
    : UserListSortDirection.Desc;
}

function headerControls(controls: SortAndFilterControls | undefined) {
  if (!controls) {
    return {
      sortableBy: () => ({}),
      filterableByRange: () => ({}),
      filterableBySpeakingLevel: () => ({}),
    };
  }

  const { sort, onSortChange, filters, onFiltersChange } = controls;

  return {
    sortableBy: (sortField: UserListSortField, sortLabels: SortLabels) => ({
      sortLabels,
      sortDirection:
        sort.sortBy === sortField
          ? toSortDirection(sort.sortDirection)
          : undefined,
      onSort: (direction: SortDirection) =>
        onSortChange({
          sortBy: sortField,
          sortDirection: toUserListSortDirection(direction),
        }),
    }),
    filterableByRange: (counterName: UserActivityCounterName) => {
      const range = filters.activityRanges[counterName] ?? {};
      return {
        isFiltered: !isEmptyRange(range),
        renderFilter: (close: () => void) => (
          <NumberRangeFilter
            value={range}
            allowDecimals={false}
            close={close}
            onApply={(nextRange) =>
              onFiltersChange({
                ...filters,
                activityRanges: {
                  ...filters.activityRanges,
                  [counterName]: nextRange,
                },
              })
            }
          />
        ),
      };
    },
    filterableBySpeakingLevel: () => ({
      isFiltered: filters.speakingLevels.length > 0,
      renderFilter: (close: () => void) => (
        <CheckboxListFilter<SpeakingLevel>
          options={SPEAKING_LEVEL_OPTIONS}
          value={filters.speakingLevels}
          close={close}
          onApply={(speakingLevels) =>
            onFiltersChange({ ...filters, speakingLevels })
          }
        />
      ),
    }),
  };
}

function RowActions({ user }: { user: UserFragmentFragment }) {
  const navigate = useNavigate();

  const actions = [
    {
      label: "Edit",
      path: `/users/${user.id}`,
      icon: PencilSquareIcon,
    },
    {
      label: "User Progress",
      path: `/users/${user.id}/lesson-feedback-report`,
      icon: ChartBarIcon,
    },
    {
      label: "Class Room Plans",
      path: `/users/${user.id}/class-room-plans`,
      icon: CalendarDaysIcon,
    },
    {
      label: "1on1 Plans",
      path: `/users/${user.id}/1on1-classroom-plans`,
      icon: CalendarDaysIcon,
    },
  ];

  return (
    <Menu as="div" className={"relative"}>
      <Menu.Button
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        className={
          "p-1.5 rounded-full hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-600"
        }
      >
        <EllipsisVerticalIcon className={"w-5 h-5"} />
      </Menu.Button>
      <Menu.Items
        anchor={"bottom end"}
        transition
        className={
          "z-50 w-48 [--anchor-gap:4px] rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none transition duration-100 ease-out data-[closed]:opacity-0"
        }
      >
        {actions.map((action) => (
          <Menu.Item key={action.path}>
            {({ active }) => (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(action.path);
                }}
                className={[
                  "w-full text-left px-4 py-2 text-sm flex items-center gap-2",
                  active ? "bg-slate-50 text-slate-900" : "text-slate-700",
                ].join(" ")}
              >
                <action.icon className={"w-4 h-4 flex-shrink-0"} />
                {action.label}
              </button>
            )}
          </Menu.Item>
        ))}
        <div className={"border-t border-slate-100"} />
        <Menu.Item>
          {({ active }) => (
            <button
              onClick={(e) => {
                e.stopPropagation();
                window.open(
                  `${import.meta.env.VITE_USER_APP_BASE_URL}/impersonate-user?uid=${user.authId}`,
                  "_blank",
                  "noopener noreferrer",
                );
              }}
              className={[
                "w-full text-left px-4 py-2 text-sm flex items-center gap-2",
                active ? "bg-slate-50 text-slate-900" : "text-slate-700",
              ].join(" ")}
            >
              <ArrowTopRightOnSquareIcon className={"w-4 h-4 flex-shrink-0"} />
              Impersonate
            </button>
          )}
        </Menu.Item>
      </Menu.Items>
    </Menu>
  );
}

function UserNameAndEmail({ user }: { user: UserFragmentFragment }) {
  const copyToClipboard = useCopyToClipboard();
  const fullName = `${user.givenName} ${user.familyName}`.trim();
  return (
    <div className={"min-w-[9rem] md:min-w-[12rem] max-w-[20rem]"}>
      <p className={"font-medium text-slate-900 truncate"} title={fullName}>
        {fullName || "—"}
      </p>
      <p className={"flex items-center gap-1 group"}>
        <span
          className={"min-w-0 text-xs text-slate-500 truncate"}
          title={user.email}
        >
          {user.email}
        </span>
        <span
          onClick={(e) => {
            e.stopPropagation();
            copyToClipboard(user.email, "Email copied");
          }}
          title={"Copy email"}
          className={
            "shrink-0 p-1 rounded-full invisible group-hover:visible cursor-pointer hover:bg-slate-200"
          }
        >
          <ClipboardIcon className={"w-3.5 h-3.5 text-slate-400"} />
        </span>
      </p>
    </div>
  );
}

export function UserTable(props: Props) {
  const users = props.users;
  const columnHelper = createColumnHelper<UserFragmentFragment>();
  const { sortableBy, filterableByRange, filterableBySpeakingLevel } =
    headerControls(props.sortAndFilterControls);
  const columns = [
    columnHelper.accessor("email", {
      id: "user",
      cell: (info) => <UserNameAndEmail user={info.row.original} />,
      header: () => (
        <TableColumnHeader
          label="User"
          description="Name and email"
          {...sortableBy(UserListSortField.Email, EMAIL_SORT_LABELS)}
        />
      ),
    }),
    columnHelper.accessor("speakingLevel", {
      cell: (info) => (
        <SpeakingLevelWithGroup
          speakingLevel={info.getValue()}
          userGroupName={info.row.original.userGroup.name}
          activePlans={props.activePlansOf?.(info.row.original.id)}
        />
      ),
      header: () => (
        <TableColumnHeader
          label="Level"
          description="Speaking level, active plans and user group"
          {...sortableBy(UserListSortField.SpeakingLevel, NUMERIC_SORT_LABELS)}
          {...filterableBySpeakingLevel()}
        />
      ),
    }),
    ...ACTIVITY_COUNTER_COLUMNS.map((counterColumn) =>
      columnHelper.display({
        id: counterColumn.id,
        meta: { className: "hidden md:table-cell" },
        cell: (info) => counterColumn.countOf(info.row.original),
        header: () => (
          <TableColumnHeader
            label={counterColumn.header}
            description={counterColumn.description}
            {...sortableBy(counterColumn.sortField, NUMERIC_SORT_LABELS)}
            {...filterableByRange(counterColumn.counterName)}
          />
        ),
      }),
    ),
    columnHelper.display({
      id: "actions",
      header: "",
      cell: (info) => <RowActions user={info.row.original} />,
    }),
  ];

  const table = useReactTable({
    columns,
    data: users as Array<UserFragmentFragment>,
    getCoreRowModel: getCoreRowModel(),
    manualFiltering: true,
  });

  function exportAsCsv() {
    const csv = json2csv(
      users.map((u) => ({
        id: u.id,
        email: u.email,
        givenName: u.givenName,
        familyName: u.familyName,
        speakingLevel: u.speakingLevel,
        participatedRLCCount: u.partecipatedSpeakingClassCount,
        participatedPCCount: u.partecipatedProgressCheckCount,
      })),
    );
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "users.csv";
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  return (
    <div>
      {props.enableCsvExport ? (
        <div className={"flex justify-end mb-2"}>
          <PrimaryButton onClick={exportAsCsv} label={"Export as CSV"} />
        </div>
      ) : null}
      <BasicTable
        table={table}
        onRowClick={props.onRowClick}
        compact
        scrollableMinWidthClassName={"md:min-w-[44rem]"}
      />
    </div>
  );
}
